import React, { useContext, useMemo, useState, createContext } from 'react';

import {
  CreditFacilityContextType,
  CreditFacilityProviderProps,
} from 'types/creditFacilities';

import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';

export const CreditFacilityContext = createContext<CreditFacilityContextType | null>(
  null,
);

export const useCreditFacilityContext = () => {
  const context = useContext(CreditFacilityContext);
  if (context === null) {
    throw new Error('CreditFacilityContext must be present');
  }

  return context;
};

const CreditFacilityProvider: React.FC<CreditFacilityProviderProps> = ({
  renderLoading,
  organizationId,
  children,
  fallbackComponent,
}) => {
  const [creditFacilityId, setCreditFacilityId] = useState<string | null>(null);
  const { data, loading } = useGetCreditFacilitiesQuery({
    variables: { organizationId },
  });

  const context = useMemo(() => {
    if (creditFacilityId) {
      return { creditFacilityId, setCreditFacilityId };
    }

    if (data && data.creditFacilities?.length > 0) {
      return {
        creditFacilityId: data.creditFacilities[0].id,
        setCreditFacilityId,
      };
    }

    return null;
  }, [creditFacilityId, setCreditFacilityId, data]);

  if (
    !loading &&
    data?.creditFacilities &&
    data.creditFacilities.length === 0
  ) {
    return fallbackComponent;
  }

  if (loading || !context) {
    return renderLoading;
  }
  return (
    <CreditFacilityContext.Provider value={context}>
      {children}
    </CreditFacilityContext.Provider>
  );
};

export default CreditFacilityProvider;
